import { useRouter } from "@tanstack/react-router";
import { type Alerta, normAlerta, type Ref } from "@vetbolso/contracts";
import { dataBR, fmt, MASCARA_DECIMAL, nomeEspecie, num } from "@vetbolso/core";
import { COR_ESPECIE, Icone, mascarar, type NomeIcone } from "@vetbolso/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { type UnPeso, useApp, useBase } from "./estado.tsx";

/** No MVP as citações ficam só no admin, para auditoria. Ligar aqui traz de volta selos, rodapés e a tela de fontes. */
export const MOSTRAR_FONTES = false;

/** Selo de fonte: o elemento-assinatura. Toque abre a fonte com o trecho conferido. */
/** `compacto` mostra só a sigla: o localizador e o trecho aparecem ao tocar. */
export function Refs({ refs, compacto = false }: { refs?: Ref[]; compacto?: boolean }) {
  const base = useBase();
  const { abrir } = useApp();
  if (!MOSTRAR_FONTES) return null;
  // Compacto: uma pílula por fonte, mesmo que a ficha cite dois trechos dela.
  const lista = compacto ? [...new Map((refs ?? []).map((r) => [r.f, r])).values()] : (refs ?? []);
  const chips = lista.flatMap((r, i) => {
    const f = base.fontes.find((x) => x.id === r.f);
    if (!f) return [];
    const nv = f.status !== "verificado" || r.status === "nao_verificado";
    return [
      <button
        // biome-ignore lint/suspicious/noArrayIndexKey: a mesma fonte pode aparecer duas vezes com localizadores diferentes
        key={`${r.f}${r.loc}${i}`}
        type="button"
        className={`ref${nv ? " nv" : ""}`}
        title={f.titulo}
        onClick={() => abrir({ t: "ref", f: r.f, loc: r.loc, cit: r.cit, nv })}
      >
        <span className="dot" />
        <span className="rt">
          {f.sigla}
          {r.loc && !compacto ? ` · ${r.loc}` : ""}
          {nv ? " · pendente" : ""}
        </span>
      </button>,
    ];
  });
  return chips.length ? <div className={`refs${compacto ? " compacto" : ""}`}>{chips}</div> : null;
}

/** Junta refs repetidas (mesma fonte e localizador), como no protótipo. */
export const refsUnicas = (lista: (Ref[] | undefined)[]) => [
  ...new Map(lista.flatMap((r) => r ?? []).map((x) => [x.f + x.loc, x])).values(),
];

export function Alertas({ lista }: { lista?: Alerta[] }) {
  return (
    <>
      {(lista ?? []).map(normAlerta).map((a, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: lista fixa da ficha
        <div key={i} className={`alert ${a.nivel === "danger" ? "danger" : a.nivel === "info" ? "info" : "warning"}`}>
          <Icone nome={a.nivel === "info" ? "info" : "alert"} className="sm" />
          <div>
            <p>{a.texto}</p>
            <Refs refs={a.refs} />
          </div>
        </div>
      ))}
    </>
  );
}

export function Carimbo() {
  const base = useBase();
  return (
    <p className="stamp">
      <Icone nome="check" className="sm" /> Base de {dataBR(base.meta?.conferidoEm)} · revisão clínica pendente
    </p>
  );
}

export const SpPill = ({ id }: { id: string }) => (
  <span className="sp-pill" style={{ color: COR_ESPECIE[id] ?? "var(--muted)" }}>
    {nomeEspecie(id)}
  </span>
);

export function Cabeca({ titulo, sub, voltar = true }: { titulo: string; sub?: string; voltar?: boolean }) {
  const router = useRouter();
  return (
    <div className="h-screen">
      {voltar ? (
        <button
          type="button"
          className="back"
          aria-label="Voltar"
          onClick={() => (window.history.length > 1 ? router.history.back() : router.navigate({ to: "/mais" }))}
        >
          <Icone nome="left" />
        </button>
      ) : null}
      <div>
        <h2>{titulo}</h2>
        {sub ? <p className="sub">{sub}</p> : null}
      </div>
    </div>
  );
}

export const Vazio = ({ children, icone = "library" }: { children: ReactNode; icone?: NomeIcone }) => (
  <div className="empty">
    <Icone nome={icone} />
    {children}
  </div>
);

// Valor em texto corrido (ex.: "0,6 g/kg de folhas frescas…") perde o corpo de número grande.
export const Grande = ({ v, l, cls = "" }: { v: ReactNode; l: ReactNode; cls?: string }) => (
  <div className={`big ${cls}${typeof v === "string" && v.length > 18 ? " longo" : ""}`}>
    <span className="v">{v}</span>
    <span className="l">{l}</span>
  </div>
);

/**
 * Entrada numérica em pt-BR ("12,5"). O texto digitado é do usuário; o número
 * só muda quando o texto vira um valor válido, como no protótipo.
 */
// No campo, sem separador de milhar: "1.000" seria lido de volta como 1.
const paraCampo = (n: number, casas: number) => n.toLocaleString("pt-BR", { maximumFractionDigits: casas, useGrouping: false });

export function CampoNumero({
  id,
  rotulo,
  valor,
  aoMudar,
  casas = 2,
  positivo = true,
  classe,
  sufixo,
}: {
  id: string;
  rotulo: string;
  valor: number | null;
  aoMudar: (n: number) => void;
  casas?: number;
  positivo?: boolean;
  /** Classe extra no campo (ex.: "curto" para o peso ao lado de um rótulo longo). */
  classe?: string;
  /** Algo colado ao fim da caixa, como o seletor de unidade. */
  sufixo?: ReactNode;
}) {
  const [texto, setTexto] = useState(valor == null ? "" : paraCampo(valor, casas));
  useEffect(() => {
    // Valor mudou por fora (outro campo de peso, cenário): reflete sem brigar com a digitação.
    setTexto((t) => (num(t) === valor ? t : valor == null ? "" : paraCampo(valor, casas)));
  }, [valor, casas]);
  const entrada = (
    <input
      id={id}
      className="num"
      inputMode="decimal"
      value={texto}
      onChange={(e) => {
        const t = mascarar(e, MASCARA_DECIMAL);
        setTexto(t);
        const n = num(t);
        if (n != null && (!positivo || n > 0)) aoMudar(n);
      }}
    />
  );
  return (
    <div className={classe ? `field ${classe}` : "field"}>
      <label htmlFor={id}>{rotulo}</label>
      {sufixo ? (
        <div className="caixa-un">
          {entrada}
          {sufixo}
        </div>
      ) : (
        entrada
      )}
    </div>
  );
}

const UNIDADES_PESO: { v: UnPeso; rotulo: string }[] = [
  { v: "kg", rotulo: "kg" },
  { v: "g", rotulo: "g" },
];

/** Peso para texto na unidade escolhida: "10 kg" ou "350 g". */
export const textoPeso = (kg: number, un: UnPeso) => (un === "g" ? `${fmt(kg * 1000, 1)} g` : `${fmt(kg, 2)} kg`);

/** Seletor de unidade colado à caixa do campo: botão com a unidade e menu com as opções. */
export function SeletorUnidade<T extends string>({
  valor,
  opcoes,
  aoMudar,
  rotulo,
}: {
  valor: T;
  opcoes: { v: T; rotulo: string }[];
  aoMudar: (v: T) => void;
  rotulo: string;
}) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: PointerEvent) => !caixa.current?.contains(e.target as Node) && setAberto(false);
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Fecha só o menu, não a folha por trás.
      e.stopImmediatePropagation();
      setAberto(false);
    };
    document.addEventListener("pointerdown", fora);
    window.addEventListener("keydown", esc, true);
    return () => {
      document.removeEventListener("pointerdown", fora);
      window.removeEventListener("keydown", esc, true);
    };
  }, [aberto]);
  return (
    <span className="seletor-un" ref={caixa}>
      <button
        type="button"
        className="seletor-un-b"
        aria-haspopup="listbox"
        aria-expanded={aberto}
        aria-label={`${rotulo}: ${opcoes.find((o) => o.v === valor)?.rotulo}`}
        onClick={() => setAberto((a) => !a)}
      >
        {opcoes.find((o) => o.v === valor)?.rotulo}
        <Icone nome="baixo" className="sm" />
      </button>
      {aberto ? (
        <span className="seletor-un-menu" role="listbox" aria-label={rotulo}>
          {opcoes.map((o) => (
            <button
              key={o.v}
              type="button"
              role="option"
              aria-selected={o.v === valor}
              onClick={() => {
                aoMudar(o.v);
                setAberto(false);
              }}
            >
              {o.rotulo}
              {o.v === valor ? <Icone nome="check" className="sm" /> : null}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Peso do paciente com a unidade ao lado. O app guarda e calcula em kg; em
 * gramas só a entrada e a exibição mudam (filhotes, aves, roedores).
 */
export function CampoPeso({
  id,
  rotulo = "Peso do paciente",
  solto,
  classe,
}: {
  id: string;
  rotulo?: string;
  solto?: boolean;
  classe?: string;
}) {
  const { peso, setPeso, unPeso, setUnPeso } = useApp();
  const g = unPeso === "g";
  const campo = (
    <CampoNumero
      id={id}
      rotulo={rotulo}
      classe={classe}
      // Arredonda para não reescrever o texto por erro de ponto flutuante (0,35 × 1000).
      valor={g ? Math.round(peso * 1e6) / 1e3 : peso}
      casas={g ? 1 : 2}
      aoMudar={(n) => setPeso(g ? n / 1000 : n)}
      sufixo={<SeletorUnidade rotulo="Unidade do peso" valor={unPeso} opcoes={UNIDADES_PESO} aoMudar={setUnPeso} />}
    />
  );
  return solto ? campo : <div className="fields">{campo}</div>;
}

export const especieNome = (e: string) => nomeEspecie(e);

// ---------- peças de lista e de folha (estilos em cartoes.css) ----------

/** Tom do tópico: define --tom, que pinta ícone, marcadores e destaques. */
export type Tom = "dose" | "apres" | "ind" | "contra" | "tox" | "aviso" | "mec" | "campo" | "inter" | "efeitos" | "fonte";

/** Seção de folha ou tela: cartão com ícone sólido, título e contagem. */
export function Secao({
  titulo,
  icone,
  tom,
  n,
  extra,
  children,
  ...resto
}: {
  titulo: ReactNode;
  icone: NomeIcone;
  tom: Tom;
  n?: number;
  /** Algo no canto do cabeçalho no lugar da contagem (valor, etiqueta). */
  extra?: ReactNode;
  children?: ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, "title">) {
  return (
    <section
      aria-label={typeof titulo === "string" ? titulo : undefined}
      {...resto}
      className={`sec t-${tom}${resto.className ? ` ${resto.className}` : ""}`}
    >
      <header className="sec-h">
        <span className="sec-ic" aria-hidden="true">
          <Icone nome={icone} />
        </span>
        <h4>{titulo}</h4>
        {extra ? <span className="sec-x">{extra}</span> : n && n > 1 ? <span className="sec-n">{n}</span> : null}
      </header>
      {children}
    </section>
  );
}

/** Grupo de cartões com título e contagem. */
export function Grupo({ titulo, n, densos, children }: { titulo: ReactNode; n?: number; densos?: boolean; children: ReactNode }) {
  return (
    <section className="grupo">
      <div className="grupo-h">
        <span className="group-label">{titulo}</span>
        {n != null ? <span className="grupo-n">{n}</span> : null}
      </div>
      <ul className={`cartoes${densos ? " densos" : ""}`}>{children}</ul>
    </section>
  );
}

/**
 * Cartão de item de lista. Com `aoTocar` vira botão com seta; `valor` fica à
 * direita do nome; `children` entra embaixo (destaque, chips, rodapé).
 */
export function Cartao({
  icone,
  tom,
  titulo,
  sub,
  pills,
  valor,
  aoTocar,
  children,
}: {
  icone?: NomeIcone;
  tom: Tom;
  titulo: ReactNode;
  sub?: ReactNode;
  pills?: ReactNode;
  valor?: ReactNode;
  aoTocar?: () => void;
  children?: ReactNode;
}) {
  const dentro = (
    <>
      <span className="item-top">
        {icone ? (
          <span className="item-ic" aria-hidden="true">
            <Icone nome={icone} />
          </span>
        ) : null}
        <span className="item-t">
          <b className="item-nome">{titulo}</b>
          {pills ? <span className="item-pills">{pills}</span> : null}
          {sub ? <span className="item-sub">{sub}</span> : null}
        </span>
        {valor ? <span className="item-v">{valor}</span> : null}
        {aoTocar ? <Icone nome="right" className="sm item-seta" /> : null}
      </span>
      {children}
    </>
  );
  return (
    <li className={`t-${tom}`}>
      {aoTocar ? (
        <button type="button" className="item" onClick={aoTocar}>
          {dentro}
        </button>
      ) : (
        <div className="item">{dentro}</div>
      )}
    </li>
  );
}

/** Faixa de destaque dentro do cartão: um valor grande com a legenda embaixo. */
export const Destaque = ({ icone, v, q }: { icone: NomeIcone; v: ReactNode; q?: ReactNode }) => (
  <span className="item-destaque">
    <Icone nome={icone} className="sm" />
    <span className="item-destaque-v">{v}</span>
    {q ? <span className="item-destaque-q">{q}</span> : null}
  </span>
);

/** Duas ou mais medidas lado a lado, com rótulo curto em cima. */
export const Pares = ({ itens }: { itens: [string, ReactNode][] }) => (
  <span className="item-pares">
    {itens.map(([r, v]) => (
      <span key={r} className="item-par">
        <span>{r}</span>
        <b>{v}</b>
      </span>
    ))}
  </span>
);

/** Rodapé de folha: fonte geral da ficha e o carimbo de conferência. */
export function Rodape({ refs, titulo = "Fonte geral da ficha" }: { refs?: Ref[]; titulo?: string }) {
  return (
    <div className="rodape">
      {MOSTRAR_FONTES && refs?.length ? (
        <>
          <span className="tag">{titulo}</span>
          <Refs refs={refs} />
        </>
      ) : null}
      <Carimbo />
    </div>
  );
}

export const Nota = ({ children, icone = "info" }: { children: ReactNode; icone?: NomeIcone }) => (
  <p className="nota">
    <Icone nome={icone} className="sm" />
    <span>{children}</span>
  </p>
);

const semAcento = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

/** Filtro de lista: casa o termo em qualquer um dos textos, sem acento nem caixa. */
export const combina = (termo: string, ...textos: (string | undefined | null)[]) => {
  const t = semAcento(termo.trim());
  return !t || semAcento(textos.filter(Boolean).join(" ")).includes(t);
};

/** Barra de busca das listas, com botão de limpar. */
export function Busca({
  valor,
  aoMudar,
  placeholder,
  rotulo = "Buscar",
}: {
  valor: string;
  aoMudar: (v: string) => void;
  placeholder: string;
  rotulo?: string;
}) {
  return (
    <label className="search">
      <Icone nome="search" />
      <input
        type="search"
        value={valor}
        onChange={(ev) => aoMudar(ev.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        aria-label={rotulo}
        enterKeyHint="search"
      />
      {valor ? (
        <button type="button" className="search-x" aria-label="Limpar busca" onClick={() => aoMudar("")}>
          <Icone nome="x" className="sm" />
        </button>
      ) : null}
    </label>
  );
}

/** Nada encontrado para o termo digitado. */
export const SemResultado = ({ termo }: { termo: string }) => (
  <Vazio icone="search">Nada encontrado para “{termo.trim()}”. Tente outro nome ou parte dele.</Vazio>
);
